<template>
  <q-page class="moderation-page">
    <header class="moderation-hero">
      <div>
        <span>CONTROLO DE QUALIDADE</span>
        <h1>Validação de anúncios</h1>
        <p>Reveja a apresentação e os dados submetidos antes de publicar cada anúncio.</p>
      </div>
      <q-btn class="refresh-button" unelevated icon="refresh" label="Actualizar fila" :loading="loading" @click="fetchPending" />
    </header>

    <section class="moderation-content">
      <div class="queue-overview" aria-label="Resumo da fila de validação">
        <article><q-icon name="pending_actions" /><div><strong>{{ queueTotal }}</strong><span>A aguardar nesta vista</span></div></article>
        <article><q-icon name="filter_alt" /><div><strong>{{ activeFilterLabel }}</strong><span>Filtro activo</span></div></article>
        <article><q-icon name="schedule" /><div><strong>{{ oldestWaiting }}</strong><span>Pedido mais antigo</span></div></article>
      </div>

      <nav class="moderation-filters" aria-label="Filtrar anúncios pendentes">
        <button v-for="option in filters" :key="option.value" type="button" :class="{ active: selectedType === option.value }" @click="selectType(option.value)">
          <q-icon :name="option.icon" size="18px" />
          {{ option.label }}
        </button>
      </nav>

      <div v-if="loading" class="moderation-grid">
        <div v-for="index in 8" :key="index" class="moderation-skeleton">
          <q-skeleton height="150px" />
          <div class="q-pa-md">
            <q-skeleton type="text" width="42%" />
            <q-skeleton type="text" width="86%" />
            <q-skeleton type="text" width="62%" />
          </div>
        </div>
      </div>
      <div v-else-if="!items.length" class="moderation-empty">
        <q-icon name="task_alt" size="58px" />
        <h2>Nenhum anúncio aguarda validação</h2>
        <p>A fila está actualizada.</p>
      </div>
      <div v-else class="moderation-grid">
        <article
          v-for="item in items"
          :key="`${item.type}-${item.id}`"
          class="moderation-card"
          :class="{ 'moderation-card--clickable': item.type === 'property' }"
          :role="item.type === 'property' ? 'link' : undefined"
          :tabindex="item.type === 'property' ? 0 : undefined"
          :aria-label="item.type === 'property' ? `Ver detalhes de ${item.title}` : undefined"
          @click="openDetails(item)"
          @keydown.enter.prevent="openDetails(item)"
          @keydown.space.prevent="openDetails(item)"
        >
          <div class="moderation-card__media">
            <img v-if="item.image_url" :src="item.image_url" :alt="item.title" />
            <q-icon v-else :name="metaFor(item).icon" size="54px" />
            <div class="moderation-card__top">
              <span class="type-badge"><q-icon :name="metaFor(item).icon" /> {{ metaFor(item).label }}</span>
              <span class="waiting-badge"><i /> Aguarda validação</span>
            </div>
          </div>
          <div class="moderation-card__body">
            <p class="listing-category">{{ item.category || metaFor(item).label }}</p>
            <h2>{{ item.title }}</h2>
            <p class="listing-location"><q-icon name="location_on" /> {{ item.location || 'Localização não indicada' }}</p>
            <strong class="listing-price">{{ formatPrice(item.price, item.currency) }}</strong>
            <div class="agent-summary">
              <span class="agent-avatar">{{ agentInitials(item.agent?.name) }}</span>
              <div><strong>{{ item.agent?.name || 'Agente não identificado' }}</strong><small>{{ item.agent?.email || 'Sem email' }}</small></div>
              <time :datetime="item.submitted_at">{{ relativeDate(item.submitted_at) }}</time>
            </div>
            <div v-if="item.type === 'property'" class="details-hint">
              <span>Ver detalhes do imóvel</span>
              <q-icon name="arrow_forward" size="16px" />
            </div>
          </div>
          <footer @click.stop @keydown.stop>
            <q-btn class="reject-button" outline icon="edit_note" label="Pedir correcção" :disable="processingKey === keyOf(item)" @click="rejectItem(item)" />
            <q-btn class="approve-button" unelevated icon="check_circle" label="Aprovar e publicar" :loading="processingKey === keyOf(item)" @click="approveItem(item)" />
          </footer>
        </article>
      </div>

      <q-pagination v-if="pagination.last_page > 1" v-model="page" :max="pagination.last_page" direction-links @update:model-value="fetchPending" />
    </section>
  </q-page>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import apiClient from '@/config/api';

const router = useRouter();
const filters = [
  { value: '', label: 'Todos', icon: 'dashboard' },
  { value: 'property', label: 'Imóveis', icon: 'home_work' },
  { value: 'accommodation', label: 'Alojamentos', icon: 'hotel' },
  { value: 'rental_vehicle', label: 'Rent-a-Car', icon: 'directions_car' },
];
const typeMeta = {
  property: { label: 'Imóvel', icon: 'home_work' },
  accommodation: { label: 'Alojamento', icon: 'hotel' },
  rental_vehicle: { label: 'Rent-a-Car', icon: 'directions_car' },
};
const items = ref([]);
const loading = ref(false);
const processingKey = ref('');
const selectedType = ref('');
const page = ref(1);
const pagination = ref({ last_page: 1 });
const keyOf = (item) => `${item.type}-${item.id}`;
const queueTotal = computed(() => pagination.value.total ?? items.value.length);
const activeFilterLabel = computed(() => filters.find((filter) => filter.value === selectedType.value)?.label || 'Todos');
const oldestWaiting = computed(() => {
  const dates = items.value.map((item) => new Date(item.submitted_at)).filter((date) => !Number.isNaN(date.getTime()));
  if (!dates.length) return 'Fila vazia';
  return relativeDate(new Date(Math.min(...dates.map((date) => date.getTime()))).toISOString());
});
const metaFor = (item) => typeMeta[item.type] || { label: 'Anúncio', icon: 'campaign' };

function openDetails(item) {
  if (item.type !== 'property' || !item.slug) return;
  router.push({ name: 'imovel-details', params: { slug: item.slug } });
}

async function fetchPending() {
  loading.value = true;
  try {
    const { data } = await apiClient.get('/moderation/pending', { params: { type: selectedType.value || undefined, page: page.value, per_page: 12 } });
    const result = data.data;
    items.value = Array.isArray(result) ? result : result.data || [];
    pagination.value = Array.isArray(result) ? { last_page: 1 } : result;
  } catch (error) {
    Swal.fire('Erro', error.response?.data?.message || 'Não foi possível carregar a fila de validação.', 'error');
  } finally {
    loading.value = false;
  }
}

function selectType(type) {
  selectedType.value = type;
  page.value = 1;
  fetchPending();
}

async function approveItem(item) {
  processingKey.value = keyOf(item);
  try {
    await apiClient.post(`/moderation/${item.type}/${item.id}/approve`);
    items.value = items.value.filter((candidate) => keyOf(candidate) !== keyOf(item));
    window.dispatchEvent(new Event('moderation-queue-updated'));
    Swal.fire({ title: 'Anúncio publicado', text: 'O agente foi notificado da aprovação.', icon: 'success', timer: 2200, showConfirmButton: false });
  } catch (error) {
    Swal.fire('Não foi possível aprovar', error.response?.data?.message || 'Tente novamente.', 'error');
  } finally {
    processingKey.value = '';
  }
}

async function rejectItem(item) {
  const result = await Swal.fire({
    title: 'Pedir correcção',
    input: 'textarea',
    inputLabel: 'Explique ao agente o que deve ser corrigido',
    inputValidator: (value) => !value?.trim() && 'Indique o motivo da devolução.',
    showCancelButton: true,
    confirmButtonText: 'Enviar ao agente',
    cancelButtonText: 'Cancelar',
  });
  if (!result.isConfirmed) return;
  processingKey.value = keyOf(item);
  try {
    await apiClient.post(`/moderation/${item.type}/${item.id}/reject`, { reason: result.value.trim() });
    items.value = items.value.filter((candidate) => keyOf(candidate) !== keyOf(item));
    window.dispatchEvent(new Event('moderation-queue-updated'));
    Swal.fire({ title: 'Correcção solicitada', text: 'O agente recebeu a indicação.', icon: 'success', timer: 2000, showConfirmButton: false });
  } catch (error) {
    Swal.fire('Erro', error.response?.data?.message || 'Não foi possível devolver o anúncio.', 'error');
  } finally {
    processingKey.value = '';
  }
}

function formatPrice(value, currency = 'MZN') {
  if (value === null || value === undefined || value === '') return 'Preço sob consulta';
  return new Intl.NumberFormat('pt-MZ', { style: 'currency', currency: currency || 'MZN', maximumFractionDigits: 0 }).format(Number(value));
}

function relativeDate(value) {
  if (!value) return 'Data não indicada';
  const elapsed = Date.now() - new Date(value).getTime();
  const hours = Math.max(0, Math.floor(elapsed / 3600000));
  if (hours < 1) return 'Há menos de 1 hora';
  if (hours < 24) return `Há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  const days = Math.floor(hours / 24);
  return `Há ${days} ${days === 1 ? 'dia' : 'dias'}`;
}

function agentInitials(name) {
  return String(name || 'GK').split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

onMounted(fetchPending);
</script>

<style scoped>
.moderation-page { min-height: 100vh; color: #172235; background: #f6f7fa; }
.moderation-hero { min-height: 158px; display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 28px clamp(20px,4vw,58px) 48px; color: #fff; background: radial-gradient(circle at 82% 10%, rgba(235,163,1,.22), transparent 28%), linear-gradient(135deg, #0b1624, #17283c); }
.moderation-hero span { color: #eba301; font-size: 10px; font-weight: 800; letter-spacing: .17em; }
.moderation-hero h1 { margin: 7px 0 5px; font-size: clamp(25px,3vw,34px); line-height: 1.15; }
.moderation-hero p { max-width: 610px; margin: 0; color: #bdc6d1; font-size: 13px; }
.refresh-button { min-height: 43px; color: #17120b; background: #eba301; font-weight: 700; }
.moderation-content { display: grid; gap: 22px; max-width: 1440px; margin: -26px auto 0; padding: 0 22px 70px; }
.queue-overview { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 14px; }
.queue-overview article { min-width: 0; display: flex; align-items: center; gap: 13px; padding: 17px 19px; border: 1px solid #e3e7ed; border-radius: 12px; background: #fff; box-shadow: 0 8px 26px rgba(13,27,42,.08); }
.queue-overview .q-icon { width: 40px; height: 40px; display: grid; place-items: center; flex: 0 0 auto; border-radius: 10px; color: #946800; background: #fff5d9; font-size: 22px; }
.queue-overview div { min-width: 0; display: grid; gap: 1px; }
.queue-overview strong { overflow: hidden; color: #172235; font-size: 19px; text-overflow: ellipsis; white-space: nowrap; }
.queue-overview span { color: #8390a1; font-size: 11px; }
.moderation-filters { display: flex; flex-wrap: wrap; gap: 8px; padding: 6px; border: 1px solid #e3e7ed; border-radius: 12px; background: #fff; }
.moderation-filters button { display: inline-flex; align-items: center; gap: 7px; min-height: 39px; padding: 0 14px; border: 0; border-radius: 8px; color: #647286; background: transparent; cursor: pointer; transition: color .18s, background .18s; }
.moderation-filters button:hover { color: #172235; background: #f4f6f9; }
.moderation-filters button.active { color: #172235; background: #fff2cb; font-weight: 700; }
.moderation-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 18px; align-items: stretch; }
.moderation-skeleton,.moderation-card { min-width: 0; overflow: hidden; border: 1px solid #e8ecf4; border-radius: 16px; background: #fff; box-shadow: 0 6px 24px rgba(13,27,42,.055); }
.moderation-skeleton { min-height: 330px; }
.moderation-card { display: grid; grid-template-rows: auto 1fr auto; transition: transform .2s, box-shadow .2s, border-color .2s; }
.moderation-card:hover { transform: translateY(-3px); box-shadow: 0 13px 34px rgba(13,27,42,.1); }
.moderation-card--clickable { cursor: pointer; }
.moderation-card--clickable:focus-visible { outline: 3px solid rgba(235,163,1,.65); outline-offset: 3px; }
.moderation-card--clickable:hover { border-color: #edc658; }
.moderation-card__media { position: relative; aspect-ratio: 16 / 10; min-height: 132px; display: grid; place-items: center; overflow: hidden; color: #d4a000; background: linear-gradient(145deg,#f4f0e8,#fbf8f1); }
.moderation-card__media > img { width: 100%; height: 100%; display: block; object-fit: cover; }
.moderation-card__media > .q-icon { font-size: 64px; }
.moderation-card__top { position: absolute; inset: 10px 10px auto; display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.type-badge,.waiting-badge { display: inline-flex; align-items: center; gap: 5px; padding: 5px 8px; border-radius: 999px; font-size: 9px; font-weight: 700; box-shadow: 0 2px 7px rgba(13,27,42,.12); }
.type-badge { color: #fff; background: rgba(13,27,42,.88); }
.waiting-badge { color: #6f5007; background: rgba(255,246,218,.96); }
.waiting-badge i { width: 6px; height: 6px; border-radius: 50%; background: #eba301; }
.moderation-card__body { min-width: 0; padding: 14px 15px 10px; }
.listing-category { margin: 0 0 4px; color: #b48600; font-size: 9px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.moderation-card h2 { min-height: 36px; margin: 0; overflow: hidden; color: #0f172a; font-size: 14px; font-weight: 700; line-height: 1.3; text-overflow: ellipsis; }
.listing-location { display: flex; align-items: center; gap: 4px; margin: 7px 0 10px; overflow: hidden; color: #7a8797; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.listing-price { display: block; color: #0d1b2a; font-size: 15px; font-weight: 800; }
.agent-summary { display: grid; grid-template-columns: 30px minmax(0,1fr); gap: 8px; align-items: center; margin-top: 12px; padding-top: 10px; border-top: 1px solid #edf0f5; }
.agent-avatar { width: 30px; height: 30px; display: grid; place-items: center; border-radius: 50%; color: #172235; background: #fff0bd; font-size: 9px; font-weight: 800; }
.agent-summary > div { min-width: 0; display: grid; }
.agent-summary strong,.agent-summary small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.agent-summary strong { font-size: 10px; }.agent-summary small { color: #8a96a6; font-size: 9px; }.agent-summary time { grid-column: 2; color: #8a96a6; font-size: 9px; }
.details-hint { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 10px; color: #946800; font-size: 10px; font-weight: 700; }
.moderation-card footer { display: grid; grid-template-columns: 1fr 1.2fr; gap: 8px; padding: 10px 14px 14px; }
.reject-button { color: #a33b3b; border-color: #efcaca; }.approve-button { color: #17120b; background: #eba301; font-weight: 700; }
.moderation-empty { padding: 80px 20px; color: #93a0af; text-align: center; border: 1px dashed #dbe1e8; border-radius: 14px; background: #fff; }
.moderation-empty h2 { margin: 13px 0 5px; color: #465568; font-size: 18px; }
.moderation-empty p { margin: 0; }
@media (max-width: 1200px) { .moderation-grid { grid-template-columns: repeat(3,minmax(0,1fr)); } }
@media (max-width: 900px) { .moderation-grid { grid-template-columns: repeat(2,minmax(0,1fr)); } }
@media (max-width: 700px) { .moderation-hero { align-items: flex-start; flex-direction: column; padding-bottom: 48px; } .refresh-button { width: 100%; } .queue-overview { grid-template-columns: 1fr; gap: 8px; } .moderation-grid { grid-template-columns: 1fr; } }
@media (max-width: 430px) { .moderation-card footer { grid-template-columns: 1fr; } .moderation-filters button { flex: 1 1 42%; justify-content: center; } }
</style>
